import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const STRSARManagement = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  // Mock STR/SAR cases data
  const cases = [
    {
      id: 'STR-2024-001',
      caseId: 'STR-2024-001',
      customerId: 'CUST-789',
      customerName: 'Michael Rodriguez',
      transactionIds: ['TXN-2024-001', 'TXN-2024-002'],
      typology: 'Terrorist Financing',
      status: 'submitted',
      narrative: 'Customer sent multiple high-value transfers to high-risk jurisdictions with inadequate source of funds explanation. Pattern consistent with terrorist financing indicators.',
      reportedAuthority: 'Financial Intelligence Unit (FIU)',
      reportedAt: '2024-11-25T14:30:00Z',
      amlOfficer: 'Sarah Johnson',
      createdAt: '2024-11-24T09:15:00Z',
      lastModified: '2024-11-25T14:30:00Z',
      evidenceCount: 5,
      hashLocked: true
    },
    {
      id: 'SAR-2024-012',
      caseId: 'SAR-2024-012',
      customerId: 'CUST-456',
      customerName: 'Zhang Wei',
      transactionIds: ['TXN-2024-089', 'TXN-2024-090', 'TXN-2024-091'],
      typology: 'Structuring',
      status: 'draft',
      narrative: 'Customer consistently making transfers just below reporting threshold. Total of $47,000 in small increments over 10 days.',
      reportedAuthority: null,
      reportedAt: null,
      amlOfficer: 'David Kim',
      createdAt: '2024-11-26T11:20:00Z',
      lastModified: '2024-11-27T08:45:00Z',
      evidenceCount: 8,
      hashLocked: false
    },
    {
      id: 'STR-2024-003',
      caseId: 'STR-2024-003',
      customerId: 'CUST-123',
      customerName: 'Maria Garcia',
      transactionIds: ['TXN-2024-034'],
      typology: 'Fraud',
      status: 'accepted',
      narrative: 'Transaction involved compromised account credentials. Customer denies initiating transfer. Investigation revealed account takeover.',
      reportedAuthority: 'Financial Intelligence Unit (FIU)',
      reportedAt: '2024-11-20T16:00:00Z',
      amlOfficer: 'Sarah Johnson',
      createdAt: '2024-11-19T13:30:00Z',
      lastModified: '2024-11-20T16:00:00Z',
      evidenceCount: 12,
      hashLocked: true
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-warning/10', text: 'text-warning', label: 'Draft' },
      submitted: { bg: 'bg-primary/10', text: 'text-primary', label: 'Submitted' },
      accepted: { bg: 'bg-success/10', text: 'text-success', label: 'Accepted by FIU' }
    };
    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getTypologyIcon = (typology) => {
    const icons = {
      'Terrorist Financing': 'AlertTriangle',
      'Structuring': 'Layers',
      'Fraud': 'ShieldAlert',
      'Money Laundering': 'Droplet'
    };
    return icons?.[typology] || 'FileWarning';
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">STR / SAR Management</h2>
            <p className="text-muted-foreground mt-1">
              Suspicious Transaction/Activity Reports
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={20} />
            <span>Create New Report</span>
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Reports</p>
                <p className="text-2xl font-bold text-foreground mt-1">{cases?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="FileText" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Submitted</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {cases?.filter(c => c?.status === 'submitted')?.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Send" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Draft</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {cases?.filter(c => c?.status === 'draft')?.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <Icon name="FileEdit" size={24} className="text-warning" />
              </div>
            </div>
          </div>
          <div className="bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Accepted</p>
                <p className="text-2xl font-bold text-foreground mt-1">
                  {cases?.filter(c => c?.status === 'accepted')?.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Cases List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Active Reports</h3>
        </div>
        <div className="divide-y divide-border">
          {cases?.map((caseItem) => (
            <div
              key={caseItem?.id}
              className="p-6 hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() => setSelectedCase(caseItem)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                      <Icon name={getTypologyIcon(caseItem?.typology)} size={20} className="text-error" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground">{caseItem?.caseId}</h4>
                      <p className="text-sm text-muted-foreground">{caseItem?.customerName} ({caseItem?.customerId})</p>
                    </div>
                  </div>
                  <div className="ml-13 space-y-2">
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="flex items-center space-x-1">
                        <Icon name="Tag" size={16} className="text-muted-foreground" />
                        <span className="text-foreground font-medium">{caseItem?.typology}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="User" size={16} className="text-muted-foreground" />
                        <span className="text-muted-foreground">Officer: {caseItem?.amlOfficer}</span>
                      </span>
                      {caseItem?.hashLocked && (
                        <span className="flex items-center space-x-1 text-success">
                          <Icon name="Lock" size={16} />
                          <span className="text-xs">Hash-Locked</span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {caseItem?.narrative}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>Created: {new Date(caseItem?.createdAt)?.toLocaleDateString()}</span>
                      {caseItem?.reportedAt && (
                        <span>Reported: {new Date(caseItem?.reportedAt)?.toLocaleString()}</span>
                      )}
                      <span>{caseItem?.evidenceCount} Evidence Attachments</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2 ml-4">
                  {getStatusBadge(caseItem?.status)}
                  <div className="flex items-center space-x-2">
                    <button className="p-2 rounded-lg hover:bg-muted border border-border transition-colors">
                      <Icon name="Eye" size={18} />
                    </button>
                    <button className="p-2 rounded-lg hover:bg-muted border border-border transition-colors">
                      <Icon name="Download" size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Create Modal Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-bold text-foreground">Create New STR/SAR Report</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-muted-foreground mb-4">
                Report creation form will be implemented here with all required fields.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Case ID</label>
                  <input
                    type="text"
                    placeholder="Auto-generated"
                    className="w-full px-4 py-2 border border-border rounded-lg bg-muted/30"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Customer ID *</label>
                  <input
                    type="text"
                    placeholder="Enter customer ID"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Typology *</label>
                  <select className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary">
                    <option>Select typology</option>
                    <option>Fraud</option>
                    <option>Terrorist Financing</option>
                    <option>Structuring</option>
                    <option>Money Laundering</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Narrative *</label>
                  <textarea
                    rows="4"
                    placeholder="Explain the suspicion in detail"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary"
                  ></textarea>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                  Save Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default STRSARManagement;